import { type FC, useMemo, useRef } from 'react';
import { useFragment, graphql } from 'react-relay';
import { Badge } from 'dibs-elements/exports/Badge.tsx';
import { Link } from 'dibs-elements/exports/Link.tsx';
import { filterNulls } from 'dibs-ts-utils/exports/filterNulls.ts';
import { isFacetApplied, getFirstAppliedFacet } from './helpers/getFacetValues.ts';
import { type FacetSearchPayload, type ModifyFacetHandler } from './types.ts';
import { useServerVars } from '../shared/ServerVarsContext.tsx';

import { type PhotosSearchBrowseRelatedDesignsContainer_photos$key } from './__generated__/PhotosSearchBrowseRelatedDesignsContainer_photos.graphql.ts';

import styles from './PhotosSearchBrowseRelatedDesignsContainer.scss';

const NUM_SHOW_FACETS = {
    MOBILE: 6,
    DESKTOP: 12, // arbitraty number to fill full row of badges
};

type Props = {
    photosRef: PhotosSearchBrowseRelatedDesignsContainer_photos$key | null | undefined;
    onRelatedDesignClick: ModifyFacetHandler;
    facetSearch: FacetSearchPayload;
};

const PhotosSearchBrowseRelatedDesignsContainer: FC<Props> = ({
    photosRef,
    onRelatedDesignClick,
    facetSearch,
}) => {
    const data = useFragment(
        graphql`
            fragment PhotosSearchBrowseRelatedDesignsContainer_photos on PhotosSearchBrowseQueryConnection {
                facets {
                    imageRoomTypeFacet {
                        label
                        name
                    }
                    imageStyleFacet {
                        label
                        name
                    }
                }
            }
        `,
        photosRef
    );
    const { isMobile } = useServerVars();
    const wrapperRef = useRef<HTMLDivElement>(null);
    const linksRef = useRef<HTMLButtonElement[]>([]);
    const wrapperRefCurrent = wrapperRef.current;

    const facets = data?.facets;
    const styleSelected = isFacetApplied(facetSearch, 'style');
    const roomSelected = isFacetApplied(facetSearch, 'room');
    const projectSelected = isFacetApplied(facetSearch, 'project');

    //conditions table https://1stdibs.atlassian.net/browse/CAM-890
    const showStyleFacet =
        (styleSelected && roomSelected && !projectSelected) ||
        (roomSelected && !styleSelected && !projectSelected) ||
        (projectSelected && styleSelected && !roomSelected);

    const showStyleLabel = styleSelected && !roomSelected && !projectSelected;

    let facetSearchLabel = getFirstAppliedFacet(facetSearch, 'room')?.label;
    if (showStyleLabel) {
        facetSearchLabel = getFirstAppliedFacet(facetSearch, 'style')?.label;
    } else if (projectSelected) {
        facetSearchLabel = getFirstAppliedFacet(facetSearch, 'project')?.label;
    }

    const displayFacets = useMemo(() => {
        // resets linksRef on facets change
        linksRef.current = [];
        const imageRoomTypeFacets = facets?.imageRoomTypeFacet || [];
        const imageStyleFacet = facets?.imageStyleFacet || [];
        let facetsToRender = (showStyleFacet ? imageStyleFacet : imageRoomTypeFacets).filter(
            filterNulls
        );

        // filter out repeating attribute combination in the related search pills.
        if ([styleSelected, roomSelected, projectSelected].filter(Boolean).length > 1) {
            const appliedFacet = showStyleFacet
                ? getFirstAppliedFacet(facetSearch, 'style')
                : getFirstAppliedFacet(facetSearch, 'room');
            facetsToRender = facetsToRender.filter(facet => {
                return facet.label !== appliedFacet?.value;
            });
        }
        return facetsToRender;
    }, [facets, facetSearch, showStyleFacet, styleSelected, roomSelected, projectSelected]);

    const getBadgeLabel = (facetName: string | null): string => {
        facetSearchLabel = facetSearchLabel || '';
        let badgeLabel = `${facetSearchLabel} ${facetName}`;
        if (showStyleFacet) {
            badgeLabel = `${facetName} ${facetSearchLabel}`;
        }
        return badgeLabel.trim();
    };

    const handleScrollEvents = (): void => {
        window.scroll({
            top: 0,
            behavior: 'smooth',
        });
        if (isMobile && wrapperRefCurrent) {
            wrapperRefCurrent.scroll({ left: 0, behavior: 'smooth' });
        }
    };

    const numShowFacets = isMobile ? NUM_SHOW_FACETS.MOBILE : NUM_SHOW_FACETS.DESKTOP;

    return (
        <div className={styles.containerWrapper}>
            <div className={styles.wrapper} ref={wrapperRef}>
                {displayFacets.slice(0, numShowFacets).map((facet, index) => {
                    if (linksRef.current[index] && wrapperRefCurrent && !isMobile) {
                        const wrapperTop = wrapperRefCurrent.getBoundingClientRect().top;
                        const childrenTop = linksRef.current[index].getBoundingClientRect().top;
                        // if element goes to next row then remove it
                        if (wrapperTop !== childrenTop) {
                            return null;
                        }
                    }
                    const { label = '', name = '' } = facet;
                    return (
                        <Link
                            key={`${label}-${index}`}
                            linkRef={el => (linksRef.current[index] = el as HTMLButtonElement)}
                            className={styles.relatedDesignItemWrapper}
                            onClick={() => {
                                onRelatedDesignClick({
                                    facetType: showStyleFacet ? 'style' : 'room',
                                    value: label || '',
                                    label: name || '',
                                });
                                handleScrollEvents();
                            }}
                        >
                            <Badge
                                dataTn="related-design-link"
                                type="pill"
                                size="medium-regular"
                                interactive
                            >
                                {getBadgeLabel(name)}
                            </Badge>
                        </Link>
                    );
                })}
            </div>
        </div>
    );
};

export default PhotosSearchBrowseRelatedDesignsContainer;
